import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiArrowDown,
  FiArrowLeft,
  FiArrowUp,
  FiEdit3,
  FiLayers,
  FiPlus,
  FiTrash2,
  FiVideo
} = FiIcons;

const escapeFilterValue = (value) => String(value || '')
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"');

const PlaylistDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [items, setItems] = useState([]);
  const [availableVideos, setAvailableVideos] = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [movingItemId, setMovingItemId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const buildCompatibleVideoFilter = (playlistData) => {
    const clauses = [
      'workflow_status != "archived"',
      `visibility_scope = "${escapeFilterValue(playlistData.visibility_scope)}"`
    ];

    const appIds = Array.isArray(playlistData.app_ids) ? playlistData.app_ids : [];
    if (appIds.length > 0) {
      const appClauses = appIds.map(
        appId => `app_ids ?= "${escapeFilterValue(appId)}"`
      );
      clauses.push(`(${appClauses.join(' || ')})`);
    }

    return clauses.join(' && ');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const playlistData = await apiCall(() => pb.collection('playlists').getOne(id, {
        expand: 'app_ids'
      }));

      const [itemsData, videosData] = await Promise.all([
        apiCall(() => pb.collection('playlist_items').getFullList({
          filter: `playlist = "${escapeFilterValue(id)}"`,
          sort: 'position,created',
          expand: 'video'
        })),
        apiCall(() => pb.collection('videos').getFullList({
          filter: buildCompatibleVideoFilter(playlistData),
          sort: 'sort_order,created',
          expand: 'app_ids'
        }))
      ]);

      setPlaylist(playlistData);
      setItems(itemsData);
      setAvailableVideos(videosData);
    } catch (loadError) {
      console.error('Error loading Topic:', loadError);
      setPlaylist(null);
      setItems([]);
      setAvailableVideos([]);
      setError('Could not load this Topic. Confirm PocketBase access and try again.');
    } finally {
      setLoading(false);
    }
  };

  const compatibleVideosNotAdded = availableVideos.filter(video =>
    !items.some(item => item.video === video.id)
  );

  const handleAddVideo = async () => {
    if (!selectedVideoId || adding) return;

    if (items.some(item => item.video === selectedVideoId)) {
      setError('This video is already in the Topic.');
      return;
    }

    if (!availableVideos.some(video => video.id === selectedVideoId)) {
      setError('This video is not compatible with the Topic scope or App targeting.');
      return;
    }

    setAdding(true);
    setError('');

    try {
      const position = items.length > 0
        ? Math.max(...items.map(item => Number(item.position) || 0)) + 1
        : 0;

      await apiCall(() => pb.collection('playlist_items').create({
        playlist: id,
        video: selectedVideoId,
        position,
        note: ''
      }));

      const newItems = await apiCall(() => pb.collection('playlist_items').getFullList({
        filter: `playlist = "${escapeFilterValue(id)}"`,
        sort: 'position,created',
        expand: 'video'
      }));

      setItems(newItems);
      setSelectedVideoId('');
    } catch (addError) {
      console.error('Failed to add video to Topic:', addError);
      const duplicate = /unique|already|exist/i.test(
        `${addError?.message || ''} ${JSON.stringify(addError?.data?.data || {})}`
      );
      setError(duplicate ? 'This video is already in the Topic.' : 'Could not add the video to this Topic.');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Remove this video from the Topic?')) return;

    try {
      setError('');
      await apiCall(() => pb.collection('playlist_items').delete(itemId));
      setItems(currentItems => currentItems.filter(item => item.id !== itemId));
    } catch (removeError) {
      console.error('Failed to remove Topic item:', removeError);
      setError('Could not remove this video from the Topic.');
    }
  };

  const handleMove = async (index, direction) => {
    if (movingItemId) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === items.length - 1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const itemA = items[index];
    const itemB = items[newIndex];
    const positionA = Number(itemA.position) || 0;
    const positionB = Number(itemB.position) || 0;

    const movedA = { ...itemA, position: positionB };
    const movedB = { ...itemB, position: positionA };
    const nextItems = [...items];
    nextItems[index] = movedB;
    nextItems[newIndex] = movedA;

    setItems(nextItems);
    setMovingItemId(itemA.id);
    setError('');

    try {
      await Promise.all([
        apiCall(() => pb.collection('playlist_items').update(itemA.id, { position: positionB })),
        apiCall(() => pb.collection('playlist_items').update(itemB.id, { position: positionA }))
      ]);
    } catch (moveError) {
      console.error('Failed to reorder Topic:', moveError);
      setError('Could not save the new Topic order. The stored order has been restored.');
      await loadData();
    } finally {
      setMovingItemId('');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-400">Loading Topic...</div>;
  }

  if (!playlist) {
    return (
      <div className="max-w-xl mx-auto p-8 bg-red-500/10 border border-red-500/20 rounded-xl text-red-200 flex items-start gap-3">
        <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <span>{error || 'Topic not found.'}</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div className="flex items-center space-x-4 min-w-0">
          <button type="button" onClick={() => navigate('/playlists')} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors flex-shrink-0">
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-blue-400 mb-1"><SafeIcon icon={FiLayers} className="w-4 h-4" /> Topic</div>
            <h1 className="text-2xl font-bold text-white truncate">{playlist.name}</h1>
            <p className="text-slate-400 text-sm mt-1 line-clamp-2">{playlist.description || 'No description'}</p>
          </div>
        </div>
        <Link to={`/playlists/${id}/edit`} className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded-lg flex items-center justify-center space-x-2 font-medium transition-colors shadow-lg shadow-blue-900/20 whitespace-nowrap">
          <SafeIcon icon={FiEdit3} className="w-4 h-4" />
          <span>Edit Topic</span>
        </Link>
      </div>

      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/20 text-red-200 px-4 py-3 rounded-xl flex items-start gap-2 text-sm">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-slate-900 rounded-xl p-5 border border-slate-800 mb-6">
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 capitalize">
            {playlist.visibility_scope?.replace(/_/g, ' ')}
          </span>
          <span className="px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 capitalize">
            {playlist.workflow_status}
          </span>
          {playlist.expand?.app_ids?.map(app => (
            <span key={app.id} className="px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300">
              {app.app_name}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800 mb-8">
        <h3 className="text-lg font-semibold text-white mb-2">Videos in this Topic</h3>
        <p className="text-sm text-slate-500 mb-5">
          Only non-archived videos matching this Topic's visibility scope and App targeting are offered. Drag-style ordering is represented by the up/down controls so the Topic can double as a guided learning sequence.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mb-6 p-4 bg-slate-950 rounded-xl border border-slate-800">
          <div className="flex-1">
            <select value={selectedVideoId} onChange={(event) => setSelectedVideoId(event.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="">Select a compatible video to add...</option>
              {compatibleVideosNotAdded.map(video => (
                <option key={video.id} value={video.id}>{video.title}</option>
              ))}
            </select>
            {compatibleVideosNotAdded.length === 0 && (
              <p className="text-xs text-slate-600 mt-2">No additional compatible videos are available.</p>
            )}
          </div>
          <button type="button" onClick={handleAddVideo} disabled={!selectedVideoId || adding} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50 transition-colors">
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>{adding ? 'Adding...' : 'Add Video'}</span>
          </button>
        </div>

        <div className="space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-10 text-slate-500 border-2 border-dashed border-slate-800 rounded-xl">
              <SafeIcon icon={FiVideo} className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              No videos in this Topic yet.
            </div>
          ) : (
            items.map((item, index) => {
              const video = item.expand?.video;
              return (
                <div key={item.id} className="flex items-center gap-4 p-4 bg-slate-800/40 rounded-2xl border border-slate-700/50 hover:border-blue-500/30 transition-colors">
                  <div className="flex flex-col space-y-1 flex-shrink-0">
                    <button type="button" onClick={() => handleMove(index, 'up')} disabled={index === 0 || Boolean(movingItemId)} className="p-1.5 text-slate-500 hover:text-white disabled:opacity-30" title="Move up">
                      <SafeIcon icon={FiArrowUp} className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => handleMove(index, 'down')} disabled={index === items.length - 1 || Boolean(movingItemId)} className="p-1.5 text-slate-500 hover:text-white disabled:opacity-30" title="Move down">
                      <SafeIcon icon={FiArrowDown} className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="w-28 sm:w-36 aspect-video bg-slate-900 rounded-xl overflow-hidden flex-shrink-0 border border-slate-700">
                    {video?.thumbnail_url ? (
                      <img src={video.thumbnail_url} className="w-full h-full object-cover" alt={`${video.title || 'Training video'} thumbnail`} loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-600"><SafeIcon icon={FiVideo} className="w-7 h-7" /></div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-semibold line-clamp-1">{video?.title || 'Unknown Video'}</h4>
                    <p className="text-xs text-slate-500 mt-1 leading-5 line-clamp-2">{video?.description || 'No description added yet.'}</p>
                    <p className="text-[10px] text-slate-500 mt-2 uppercase tracking-wider">
                      Position {item.position}
                      {video?.category ? ` • ${video.category.replaceAll('_', ' ')}` : ''}
                      {video?.duration_seconds ? ` • ${Math.floor(video.duration_seconds / 60)}m` : ''}
                    </p>
                  </div>

                  <button type="button" onClick={() => handleRemoveItem(item.id)} className="p-2 text-slate-500 hover:text-red-400 transition-colors flex-shrink-0" title="Remove from Topic">
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaylistDetail;